/**
 * Reporte de errores del navegador a Sentry (o compatible) cuando existe
 * `VITE_SENTRY_DSN`; sin DSN no hace nada. Captura los errores no manejados
 * (`error`, `unhandledrejection`) y los que atrapan las fronteras de error de
 * React. Solo envía el id del usuario y la ruta sin identificadores; los
 * datos sensibles se limpian con el mismo depurador del dominio que usa el
 * API.
 */
import { prepareErrorReport } from '@tumin/domain'
import { ApiError } from '@/lib/api'
import { useAuthStore } from '@/lib/auth'
import { APP_ENVIRONMENT, SENTRY_DSN, SENTRY_RELEASE } from '@/lib/env'

/** Origen de los eventos de la web. */
const WEB_SOURCE = 'web'
/**
 * Máximo de eventos por carga de página: un error dentro de un ciclo de
 * render no debe inundar el servicio ni gastar los datos móviles del asesor.
 */
export const MAX_EVENTS_PER_PAGE = 10
/** Segmentos de ruta que son identificadores (UUID o números): se reemplazan por `:id`. */
const ID_SEGMENT = /\/(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|\d+)(?=\/|$)/gi
/** Guiones de un UUID (el id de evento son 32 caracteres hexadecimales). */
const UUID_DASHES = /-/g

let sentEvents = 0
const reported = new WeakSet<object>()

/** Ruta sin identificadores: `/loans/3f…/pagos` → `/loans/:id/pagos`. */
export function routeTemplate(pathname: string): string {
  return pathname.replace(ID_SEGMENT, '/:id')
}

/**
 * ¿Vale la pena reportar este error? Los del API ya los registra el
 * servidor (y los 4xx son respuestas esperadas); los de red dependen de la
 * señal del usuario.
 */
export function isReportable(error: unknown): boolean {
  return !(error instanceof ApiError)
}

/** Opciones del envío (inyectables en pruebas). */
interface ReportOptions {
  dsn?: string | undefined
  fetcher?: typeof fetch
}

/**
 * Envía el error al servicio de monitoreo. Devuelve la promesa del envío o
 * `null` si no se envió (sin DSN, no reportable, repetido o límite alcanzado).
 */
export function reportClientError(error: unknown, options: ReportOptions = {}): Promise<void> | null {
  const dsn = options.dsn ?? SENTRY_DSN
  if (!dsn || !isReportable(error) || sentEvents >= MAX_EVENTS_PER_PAGE) return null
  if (typeof error === 'object' && error !== null) {
    if (reported.has(error)) return null
    reported.add(error)
  }
  const report = prepareErrorReport(
    dsn,
    error,
    {
      environment: APP_ENVIRONMENT,
      release: SENTRY_RELEASE,
      source: WEB_SOURCE,
      route: routeTemplate(window.location.pathname),
      userId: useAuthStore.getState().employee?.id,
    },
    new Date(),
    crypto.randomUUID().replace(UUID_DASHES, ''),
  )
  if (!report) return null
  sentEvents += 1
  const fetcher = options.fetcher ?? fetch
  // Sin encabezados propios: el navegador lo envía como texto simple, sin preflight CORS.
  return fetcher(report.url, { method: 'POST', body: report.body, keepalive: true })
    .then(() => undefined)
    .catch(() => undefined)
}

/** Reinicia el contador de eventos (solo pruebas). */
export function resetReportedEventsForTests(): void {
  sentEvents = 0
}

/** Escucha los errores no manejados del navegador y los reporta. Llamar una vez al iniciar. */
export function installGlobalErrorReporting(): void {
  if (!SENTRY_DSN) return
  window.addEventListener('error', (event) => {
    void reportClientError(event.error ?? event.message)
  })
  window.addEventListener('unhandledrejection', (event) => {
    void reportClientError(event.reason)
  })
}
